import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/api";
import { errorMessage } from "@/api/client";
import type { RecipientList, RecipientListInput } from "@/api/types";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  ErrorBanner,
  Field,
  inputClass,
  PageHeader,
  Spinner,
} from "@/components/ui";
import { findInvalidEmails, parseEmails } from "@/lib/emails";

const EMPTY: RecipientListInput = {
  name: "",
  reportType: "compliance",
  emails: [],
  enabled: true,
};

function RecipientForm({
  initial,
  onSubmit,
  onCancel,
  busy,
}: {
  initial: RecipientListInput;
  onSubmit: (v: RecipientListInput) => void;
  onCancel: () => void;
  busy: boolean;
}) {
  const [form, setForm] = useState(initial);
  const [emailText, setEmailText] = useState(initial.emails.join("\n"));
  const emails = parseEmails(emailText);
  const invalid = findInvalidEmails(emails);
  const canSave =
    form.name.trim() !== "" && emails.length > 0 && invalid.length === 0;

  return (
    <Card title={initial.name ? "Edit list" : "New list"}>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Name">
          <input
            className={inputClass}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field label="Report type">
          <select
            className={inputClass}
            value={form.reportType}
            onChange={(e) =>
              setForm({
                ...form,
                reportType: e.target.value as RecipientListInput["reportType"],
              })
            }
          >
            <option value="compliance">Compliance</option>
            <option value="weekly">Weekly</option>
          </select>
        </Field>
      </div>
      <div className="mt-4">
        <Field label="Emails (one per line or comma-separated)">
          <textarea
            rows={6}
            className={inputClass}
            value={emailText}
            onChange={(e) => setEmailText(e.target.value)}
          />
        </Field>
        {invalid.length > 0 && (
          <p className="mt-1 text-sm text-red-600">
            Invalid: {invalid.join(", ")}
          </p>
        )}
      </div>
      <label className="mt-4 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.enabled}
          onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
        />
        Enabled
      </label>
      <div className="mt-4 flex gap-2">
        <Button
          disabled={!canSave || busy}
          onClick={() => onSubmit({ ...form, name: form.name.trim(), emails })}
        >
          Save
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}

export default function RecipientsPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<RecipientList | "new" | null>(null);
  const [deleting, setDeleting] = useState<RecipientList | null>(null);

  const lists = useQuery({
    queryKey: ["recipients"],
    queryFn: api.listRecipients,
  });
  const done = () => qc.invalidateQueries({ queryKey: ["recipients"] });

  const save = useMutation({
    mutationFn: (v: RecipientListInput) =>
      editing && editing !== "new"
        ? api.updateRecipient(editing.id, v)
        : api.createRecipient(v),
    onSuccess: () => {
      setEditing(null);
      return done();
    },
  });
  const toggle = useMutation({
    mutationFn: ({ id, ...rest }: RecipientList) =>
      api.updateRecipient(id, { ...rest, enabled: !rest.enabled }),
    onSuccess: done,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteRecipient(id),
    onSuccess: () => {
      setDeleting(null);
      return done();
    },
  });

  const err = [lists.error, save.error, toggle.error, remove.error].find(
    Boolean,
  );

  return (
    <>
      <PageHeader
        title="Recipients"
        actions={
          !editing && (
            <Button onClick={() => setEditing("new")}>New list</Button>
          )
        }
      />
      <ErrorBanner message={err ? errorMessage(err) : null} />
      {editing && (
        <div className="mb-6">
          <RecipientForm
            key={editing === "new" ? "new" : editing.id}
            initial={editing === "new" ? EMPTY : editing}
            busy={save.isPending}
            onSubmit={(v) => save.mutate(v)}
            onCancel={() => setEditing(null)}
          />
        </div>
      )}
      {lists.isPending ? (
        <Spinner />
      ) : (
        <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="bg-neutral-100 text-neutral-600">
              <tr>
                <th className="p-3">Name</th>
                <th className="p-3">Type</th>
                <th className="p-3">Recipients</th>
                <th className="p-3">Enabled</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {lists.data?.map((l) => (
                <tr key={l.id} className="border-t border-neutral-200">
                  <td className="p-3 font-medium">{l.name}</td>
                  <td className="p-3">
                    <Badge>{l.reportType}</Badge>
                  </td>
                  <td className="p-3">{l.emails.length}</td>
                  <td className="p-3">
                    <input
                      type="checkbox"
                      aria-label={`Enable ${l.name}`}
                      checked={l.enabled}
                      disabled={toggle.isPending}
                      onChange={() => toggle.mutate(l)}
                    />
                  </td>
                  <td className="flex justify-end gap-2 p-3">
                    <Button variant="secondary" onClick={() => setEditing(l)}>
                      Edit
                    </Button>
                    <Button variant="danger" onClick={() => setDeleting(l)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
              {lists.data?.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-neutral-500">
                    No recipient lists yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {deleting && (
        <ConfirmDialog
          title="Delete recipient list?"
          message={`"${deleting.name}" will be permanently removed.`}
          confirmLabel="Delete"
          danger
          busy={remove.isPending}
          onConfirm={() => remove.mutate(deleting.id)}
          onCancel={() => setDeleting(null)}
        />
      )}
    </>
  );
}
